import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { adminAPI } from '../../utils/api';
import { formatDate, getStatusBadgeClass, formatStatusText } from '../../utils/helpers';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import AdminTableToolbar from '../../components/admin/AdminTableToolbar';
import AdminPagination from '../../components/admin/AdminPagination';
import AdminTableLoadingOverlay from '../../components/admin/AdminTableLoadingOverlay';
import { useDebounce } from '../../hooks/useDebounce';

const FETCH_LIMIT = 500;

const AdminWithdrawals = () => {
  const [rawWithdrawals, setRawWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    fetchWithdrawals();
  }, [debouncedSearch]);

  const fetchWithdrawals = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getWithdrawals({
        search: debouncedSearch || undefined,
        skip: 0,
        limit: FETCH_LIMIT,
      });
      setRawWithdrawals(response.data.withdrawals);
      setPage(1);
    } catch (error) {
      toast.error('Failed to load withdrawals');
    } finally {
      setLoading(false);
    }
  };

  const filteredWithdrawals = statusFilter
    ? rawWithdrawals.filter((w) => w.status === statusFilter)
    : rawWithdrawals;
  const total = filteredWithdrawals.length;
  const withdrawals = filteredWithdrawals.slice((page - 1) * pageSize, page * pageSize);

  const handleWithdrawalAction = async (withdrawalId, status) => {
    try {
      await adminAPI.withdrawalAction({ withdrawal_id: withdrawalId, status, txid: 'TXN-' + Date.now() });
      toast.success('Withdrawal updated');
      fetchWithdrawals();
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const filterOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'processing', label: 'Processing' },
    { value: 'completed', label: 'Completed' },
    { value: 'rejected', label: 'Rejected' },
  ];

  if (loading && rawWithdrawals.length === 0) return <div className="flex items-center justify-center min-h-[60vh]"><div className="loading-spinner"></div></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="admin-withdrawals">
      <h1 className="text-2xl sm:text-4xl font-bold text-foreground">Withdrawal Management</h1>

      <AdminTableToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by user name or email..."
        filterValue={statusFilter}
        onFilterChange={(v) => { setStatusFilter(v); setPage(1); }}
        filterOptions={filterOptions}
        filterPlaceholder="All statuses"
        total={total}
        loading={loading}
      />

      <Card className="glass-card p-6 overflow-hidden">
        <AdminTableLoadingOverlay loading={loading}>
        <div className="admin-table-wrapper">
          <table>
            <thead>
              <tr className="border-b border-border">
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">User</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Asset</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Amount</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Destination</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Status</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Date</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {withdrawals.map((withdrawal) => (
                <tr key={withdrawal.id} className="table-row" data-testid={`withdrawal-row-${withdrawal.id}`}>
                  <td className="py-4">
                    <div>
                      <p className="text-foreground">{withdrawal.user?.full_name || '—'}</p>
                      <p className="text-muted-foreground text-sm">{withdrawal.user?.email || '—'}</p>
                    </div>
                  </td>
                  <td className="py-4 text-foreground">{withdrawal.asset_symbol}</td>
                  <td className="py-4 text-foreground">{withdrawal.amount}</td>
                  <td className="py-4 text-muted-foreground text-sm">{withdrawal.destination}</td>
                  <td className="py-4">
                    <span className={`badge ${getStatusBadgeClass(withdrawal.status)}`}>
                      {formatStatusText(withdrawal.status)}
                    </span>
                  </td>
                  <td className="py-4 text-muted-foreground text-sm">{formatDate(withdrawal.created_at)}</td>
                  <td className="py-4">
                    {withdrawal.status === 'pending' && (
                      <div className="flex space-x-2">
                        <Button size="sm" onClick={() => handleWithdrawalAction(withdrawal.id, 'completed')} className="btn-primary" data-testid={`approve-withdrawal-btn-${withdrawal.id}`}>
                          Approve
                        </Button>
                        <Button size="sm" onClick={() => handleWithdrawalAction(withdrawal.id, 'rejected')} className="bg-red-600 hover:bg-red-700" data-testid={`reject-withdrawal-btn-${withdrawal.id}`}>
                          Reject
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <AdminPagination page={page} pageSize={pageSize} onPageSizeChange={(v) => { setPageSize(v); setPage(1); }} total={total} onPageChange={setPage} loading={loading} />
        </AdminTableLoadingOverlay>
      </Card>
    </div>
  );
};

export default AdminWithdrawals;
